/**
 * PermissionsTab Component
 * Role-based permission matrix management
 */

import React, { useState } from 'react';
import { Shield, Check, X, Save, RefreshCw, Info } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, Button, LoadingSpinner } from '../../../components/common';
import apiClient from '../../../services/api';

// Permission matrix structure
interface Permission {
  id?: string;
  role: string;
  resource: string;
  action: string;
  allowed: boolean;
}

interface PermissionMatrix {
  [role: string]: {
    [resource: string]: {
      [action: string]: boolean;
    };
  };
}

// Define available roles (excluding super_admin which has all permissions)
const ROLES = [
  { id: 'account_admin', name: 'Account Admin', description: 'Full organization access' },
  { id: 'manager', name: 'Manager', description: 'Team management access' },
  { id: 'employee', name: 'Employee', description: 'Personal OKR access' },
  { id: 'viewer', name: 'Viewer', description: 'Read-only access' },
];

// Define resources and their actions
const RESOURCES = [
  {
    id: 'okr',
    name: 'OKRs',
    actions: [
      { id: 'create', name: 'Create' },
      { id: 'read', name: 'View' },
      { id: 'update', name: 'Edit' },
      { id: 'delete', name: 'Delete' },
    ],
  },
  {
    id: 'key_result',
    name: 'Key Results',
    actions: [
      { id: 'create', name: 'Create' },
      { id: 'read', name: 'View' },
      { id: 'update', name: 'Update Progress' },
      { id: 'delete', name: 'Delete' },
    ],
  },
  {
    id: 'user',
    name: 'Users',
    actions: [
      { id: 'create', name: 'Invite' },
      { id: 'read', name: 'View' },
      { id: 'update', name: 'Edit' },
      { id: 'delete', name: 'Delete' },
    ],
  },
  {
    id: 'approval',
    name: 'Approvals',
    actions: [
      { id: 'create', name: 'Submit' },
      { id: 'read', name: 'View' },
      { id: 'update', name: 'Approve/Reject' },
    ],
  },
  {
    id: 'comment',
    name: 'Comments',
    actions: [
      { id: 'create', name: 'Create' },
      { id: 'read', name: 'View' },
      { id: 'delete', name: 'Delete Own' },
    ],
  },
  {
    id: 'report',
    name: 'Reports',
    actions: [
      { id: 'read', name: 'View' },
      { id: 'export', name: 'Export' },
    ],
  },
  {
    id: 'check_in',
    name: 'Check-ins',
    actions: [
      { id: 'create', name: 'Submit' },
      { id: 'read', name: 'View' },
      { id: 'update', name: 'Update' },
      { id: 'configure', name: 'Configure Schedule' },
    ],
  },
  {
    id: 'settings',
    name: 'Settings',
    actions: [
      { id: 'read', name: 'View' },
      { id: 'update', name: 'Edit' },
    ],
  },
];

// Default permissions matrix
const DEFAULT_PERMISSIONS: PermissionMatrix = {
  account_admin: {
    okr: { create: true, read: true, update: true, delete: true },
    key_result: { create: true, read: true, update: true, delete: true },
    user: { create: true, read: true, update: true, delete: true },
    approval: { create: true, read: true, update: true },
    comment: { create: true, read: true, delete: true },
    report: { read: true, export: true },
    check_in: { create: true, read: true, update: true, configure: true },
    settings: { read: true, update: true },
  },
  manager: {
    okr: { create: true, read: true, update: true, delete: false },
    key_result: { create: true, read: true, update: true, delete: false },
    user: { create: true, read: true, update: false, delete: false },
    approval: { create: true, read: true, update: true },
    comment: { create: true, read: true, delete: true },
    report: { read: true, export: true },
    check_in: { create: true, read: true, update: true, configure: true },
    settings: { read: true, update: false },
  },
  employee: {
    okr: { create: true, read: true, update: true, delete: false },
    key_result: { create: true, read: true, update: true, delete: false },
    user: { create: false, read: true, update: false, delete: false },
    approval: { create: true, read: true, update: false },
    comment: { create: true, read: true, delete: true },
    report: { read: true, export: false },
    check_in: { create: true, read: true, update: true, configure: false },
    settings: { read: false, update: false },
  },
  viewer: {
    okr: { create: false, read: true, update: false, delete: false },
    key_result: { create: false, read: true, update: false, delete: false },
    user: { create: false, read: true, update: false, delete: false },
    approval: { create: false, read: true, update: false },
    comment: { create: false, read: true, delete: false },
    report: { read: true, export: false },
    check_in: { create: false, read: true, update: false, configure: false },
    settings: { read: false, update: false },
  },
};

// Deep clone permissions to avoid mutation issues
const clonePermissions = (source: PermissionMatrix): PermissionMatrix => {
  const clone: PermissionMatrix = {};
  Object.entries(source).forEach(([role, resources]) => {
    clone[role] = {};
    Object.entries(resources).forEach(([resource, actions]) => {
      clone[role][resource] = { ...actions };
    });
  });
  return clone;
};

export const PermissionsTab: React.FC = () => {
  // Use a cloned version of DEFAULT_PERMISSIONS to avoid mutation
  const [permissions, setPermissions] = useState<PermissionMatrix>(() => clonePermissions(DEFAULT_PERMISSIONS));
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch current permissions
  const { isLoading, refetch } = useQuery({
    queryKey: ['permissions'],
    queryFn: async () => {
      try {
        const response = await apiClient.get<{ success: boolean; data: { permissions: Permission[] } }>('/permissions');
        if (response.data.success && response.data.data.permissions.length > 0) {
          // Convert flat permissions to matrix - use deep clone to avoid mutation
          const matrix = clonePermissions(DEFAULT_PERMISSIONS);
          response.data.data.permissions.forEach((p) => {
            if (matrix[p.role] && matrix[p.role][p.resource]) {
              matrix[p.role][p.resource][p.action] = p.allowed;
            }
          });
          setPermissions(matrix);
          return matrix;
        }
        // If no permissions exist, use defaults (deep clone)
        const defaults = clonePermissions(DEFAULT_PERMISSIONS);
        setPermissions(defaults);
        return defaults;
      } catch {
        // Permissions API not available yet, use defaults
        const defaults = clonePermissions(DEFAULT_PERMISSIONS);
        setPermissions(defaults);
        return defaults;
      }
    },
  });

  // Save permissions mutation
  const savePermissions = useMutation({
    mutationFn: async (matrix: PermissionMatrix) => {
      // Convert matrix to flat permissions array for bulk update
      const updates: { role: string; resource: string; action: string; allowed: boolean }[] = [];
      Object.entries(matrix).forEach(([role, resources]) => {
        Object.entries(resources).forEach(([resource, actions]) => {
          Object.entries(actions).forEach(([action, allowed]) => {
            updates.push({ role, resource, action, allowed });
          });
        });
      });

      const response = await apiClient.put('/permissions/bulk', { updates });
      return response.data;
    },
    onSuccess: async () => {
      // Force refetch to get fresh data from server
      await refetch();
      toast.success('Permissions saved successfully');
      setHasChanges(false);
    },
    onError: () => {
      toast.error('Failed to save permissions');
    },
  });

  const togglePermission = (role: string, resource: string, action: string) => {
    setPermissions((prev) => {
      // Deep clone to ensure immutable update
      const newPermissions = clonePermissions(prev);
      if (newPermissions[role] && newPermissions[role][resource]) {
        newPermissions[role][resource][action] = !prev[role][resource][action];
      }
      return newPermissions;
    });
    setHasChanges(true);
  };

  // Reset permissions mutation
  const resetPermissionsMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post('/permissions/reset');
      return response.data;
    },
    onSuccess: async () => {
      // Force refetch to get fresh data from server
      await refetch();
      toast.success('Permissions reset to defaults');
      setHasChanges(false);
    },
    onError: () => {
      toast.error('Failed to reset permissions');
    },
  });

  const resetToDefaults = () => {
    if (window.confirm('Reset all permissions to defaults? This cannot be undone.')) {
      resetPermissionsMutation.mutate();
    }
  };

  const handleSave = () => {
    savePermissions.mutate(permissions);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Permissions</h2>
          <p className="text-sm text-stone-500">
            Configure what each role can do in your organization
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={resetToDefaults}
            disabled={resetPermissionsMutation.isPending}
          >
            {resetPermissionsMutation.isPending ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset to Defaults
              </>
            )}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!hasChanges || savePermissions.isPending}
          >
            {savePermissions.isPending ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="p-4 bg-primary-50 border-primary-200">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-primary-800">
            <p className="font-medium">About Permissions</p>
            <p className="mt-1">
              Permissions control what actions each role can perform. Super Admins always have full access.
              Changes will affect all users with the modified role.
            </p>
            <p className="mt-2">
              <strong>Note:</strong> Employee OKR permissions (create/update/delete) are ownership-based.
              Employees can only create individual OKRs for themselves and can only modify OKRs they own.
            </p>
          </div>
        </div>
      </Card>

      {/* Permission Matrix */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-sand">
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider sticky left-0 bg-sand z-10">
                  Resource / Action
                </th>
                {ROLES.map((role) => (
                  <th
                    key={role.id}
                    className="px-4 py-3 text-center text-xs font-medium text-stone-500 uppercase tracking-wider min-w-[100px]"
                  >
                    <div className="flex flex-col items-center">
                      <span>{role.name}</span>
                      <span className="text-[10px] font-normal normal-case text-stone-400">
                        {role.description}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-stone-200">
              {RESOURCES.map((resource) => (
                <React.Fragment key={resource.id}>
                  {/* Resource header row */}
                  <tr className="bg-sand">
                    <td
                      colSpan={ROLES.length + 1}
                      className="px-4 py-2 text-sm font-semibold text-stone-900"
                    >
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-stone-400" />
                        {resource.name}
                      </div>
                    </td>
                  </tr>
                  {/* Action rows */}
                  {resource.actions.map((action) => (
                    <tr key={`${resource.id}-${action.id}`} className="hover:bg-sand">
                      <td className="px-4 py-2 text-sm text-stone-600 pl-10 sticky left-0 bg-white z-10">
                        {action.name}
                      </td>
                      {ROLES.map((role) => {
                        const isAllowed = permissions[role.id]?.[resource.id]?.[action.id] ?? false;
                        return (
                          <td key={role.id} className="px-4 py-2 text-center">
                            <button
                              onClick={() => togglePermission(role.id, resource.id, action.id)}
                              className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                                isAllowed
                                  ? 'bg-success-100 text-success-600 hover:bg-success-200'
                                  : 'bg-stone-100 text-stone-400 hover:bg-stone-200'
                              }`}
                              title={isAllowed ? 'Allowed - Click to deny' : 'Denied - Click to allow'}
                            >
                              {isAllowed ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <X className="w-4 h-4" />
                              )}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-6 text-sm text-stone-600">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-success-100 rounded-full flex items-center justify-center">
            <Check className="w-3 h-3 text-success-600" />
          </div>
          <span>Allowed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-stone-100 rounded-full flex items-center justify-center">
            <X className="w-3 h-3 text-stone-400" />
          </div>
          <span>Denied</span>
        </div>
      </div>
    </div>
  );
};
